import { HttpRequestError } from '../shared/request-error.js';
import { ILLUSTRATED_MARKERS } from '../shared/illustrated-markers.js';

export const LANDMARK_MARKERS = ILLUSTRATED_MARKERS.map(marker => ({ ...marker, src: `/marker-icons/${marker.name}.png` }));
const images = new Map<string, HTMLImageElement>();
const sources = new Map<string, string>();
const pendingSources = new Map<string, Promise<void>>();
const pendingImages = new Map<string, Promise<void>>();
let revision = 0;
let onReady = () => {};

export function landmarkMarker(name: string) { return LANDMARK_MARKERS.find(marker => marker.name === name); }
export function landmarkImageRevision() { return revision; }
export function onLandmarkImageReady(callback: () => void) { onReady = callback; }
export function landmarkSvgSource(name: string) { return sources.get(name) ?? landmarkMarker(name)?.src ?? ''; }

async function loadSource(name: string): Promise<void> {
  const marker = landmarkMarker(name);
  if (!marker || sources.has(name)) return;
  const response = await fetch(marker.src);
  if (!response.ok) throw new HttpRequestError(`Could not load marker artwork: ${name}`, response, 'marker-artwork');
  const bytes = new Uint8Array(await response.arrayBuffer());
  sources.set(name, `data:image/png;base64,${btoa(String.fromCharCode(...bytes))}`);
}

export async function prepareLandmarkSources(names: readonly string[]): Promise<void> {
  await Promise.all([...new Set(names)].filter(name => landmarkMarker(name)).map(name => {
    if (!pendingSources.has(name)) pendingSources.set(name, loadSource(name).catch(error => { pendingSources.delete(name); throw error; }));
    return pendingSources.get(name);
  }));
}

async function loadImage(name: string): Promise<void> {
  await prepareLandmarkSources([name]);
  const image = new Image();
  image.src = landmarkSvgSource(name);
  await image.decode();
  images.set(name, image);
  revision += 1;
  onReady();
}

export async function prepareLandmarkMarkers(names: readonly string[]): Promise<void> {
  await Promise.all([...new Set(names)].filter(name => landmarkMarker(name)).map(name => {
    if (!pendingImages.has(name)) pendingImages.set(name, loadImage(name).catch(error => { pendingImages.delete(name); throw error; }));
    return pendingImages.get(name);
  }));
}

export function landmarkImage(name: string) {
  if (!images.has(name) && !pendingImages.has(name)) void prepareLandmarkMarkers([name]).catch(error => console.warn('Marker image failed to load', error));
  return images.get(name);
}
